/**
 * What the player is holding, from every source at once (docs/spec/04 §7): keys, a pad, the touch
 * buttons, a test. Of left and right, the one pressed last wins. Pure: sources call `set` with
 * their own ids.
 */

import type { Input } from '../sim';

export type GameKey = 'left' | 'right' | 'fire' | 'bomb';

export class GameKeys {
  /** Held directions, oldest first: [source id, direction]. */
  private dirs: [string, 'left' | 'right'][] = [];
  private fire = new Set<string>();
  private bomb = new Set<string>();
  /** A tap that came and went between two ticks still counts once. */
  private fireTapped = false;
  private bombTapped = false;
  private pulse = 0;

  set(source: string, key: GameKey, down: boolean): void {
    if (key === 'left' || key === 'right') {
      const id = `${source}/${key}`;
      this.dirs = this.dirs.filter(([s]) => s !== id);
      if (down) this.dirs.push([id, key]);
    } else if (key === 'fire') {
      if (down) {
        this.fire.add(source);
        this.fireTapped = true;
      } else this.fire.delete(source);
    } else if (down) {
      this.bomb.add(source);
      this.bombTapped = true;
    } else this.bomb.delete(source);
  }

  /** Let go of everything `source` holds. */
  release(source: string): void {
    this.dirs = this.dirs.filter(([s]) => !s.startsWith(`${source}/`));
    this.fire.delete(source);
    this.bomb.delete(source);
  }

  /** Forget everything held (the window lost focus, a menu opened). */
  reset(): void {
    this.dirs = [];
    this.fire.clear();
    this.bomb.clear();
    this.fireTapped = false;
    this.bombTapped = false;
  }

  get fireHeld(): boolean {
    return this.fire.size > 0;
  }

  /**
   * The input for the tick about to run; clears the taps. `turbo` pulses a held fire button, three
   * ticks on and three off, so that holding it fires as fast as the two-shot rule allows.
   */
  take(turbo: boolean): Input {
    const dir = this.dirs[this.dirs.length - 1]?.[1];
    const held = this.fire.size > 0;
    let fire = held || this.fireTapped;
    if (turbo && held) {
      this.pulse = (this.pulse + 1) % 6;
      fire = this.pulse < 3;
    } else this.pulse = 0;
    const bomb = this.bomb.size > 0 || this.bombTapped;
    this.fireTapped = false;
    this.bombTapped = false;
    return { left: dir === 'left', right: dir === 'right', fire, bomb };
  }
}
